import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  where,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { app, auth, db, functions } from "./firebase";
import { addClinic, fetchClinics } from "./ClinicService";
import { addService, fetchServices } from "./ServiceManagementService";
import { savePredefinedRolesToFirestore } from "./RoleManagementService";
import { handleSaveUser, fetchUsersByEmail } from "./StaffManagementService";
import { generateKeywords, generateKeywordsByCharacters } from "../components/utils/Helpers";

const HOSPITAL_COLLECTION = "hospital";
const USERS_COLLECTION = "users";

export const DEPLOYMENT_MODES = {
  ONLINE: "ONLINE",
  OFFLINE: "OFFLINE",
  HYBRID: "HYBRID",
};

export const ACTIVATION_TYPES = {
  CLOUD_ACTIVATION: "CLOUD_ACTIVATION",
  OFFLINE_LICENSE_FILE: "OFFLINE_LICENSE_FILE",
};

export const LOCAL_SERVER_STATUSES = {
  NOT_INSTALLED: "NOT_INSTALLED",
  PENDING_ACTIVATION: "PENDING_ACTIVATION",
  ACTIVE: "ACTIVE",
  SUSPENDED: "SUSPENDED",
};

export const LICENSE_STATUSES = {
  TRIAL: "TRIAL",
  ACTIVE: "ACTIVE",
  SUSPENDED: "SUSPENDED",
  EXPIRED: "EXPIRED",
};

export const DEFAULT_MODULES_ENABLED = {
  clinical: true,
  billing: true,
  laboratory: true,
  radiology: true,
  pharmacy: true,
  admissions: true,
  familyFolders: true,
  pacsDicom: false,
};

export const NUMBER_RANGE_TYPES = [
  "patientNumbers",
  "invoiceNumbers",
  "receiptNumbers",
  "labNumbers",
  "familyFolderNumbers",
];

const normalizeText = (value) => String(value || "").trim();
const normalizeEmail = (value) => normalizeText(value).toLowerCase();
const normalizeDeploymentMode = (value) => {
  const mode = normalizeText(value).toUpperCase().replace(/[\s-]+/g, "_");
  if (mode === "HYBRID" || mode === "HYBRID_LOCAL_SERVER" || mode === "HYBRID_MODE") return DEPLOYMENT_MODES.HYBRID;
  if (mode === "OFFLINE" || mode === "LOCAL" || mode === "LOCAL_EDITION" || mode === "OFFLINE_ONLY") return DEPLOYMENT_MODES.OFFLINE;
  if (mode === "ONLINE" || mode === "CLOUD" || mode === "CLOUD_ONLY" || mode === "ONLINE_ONLY") return DEPLOYMENT_MODES.ONLINE;
  return "";
};
const normalizePhonePassword = (value) => normalizeText(value).replace(/\D/g, "");
const unique = (items) => Array.from(new Set((items || []).filter(Boolean)));
const ensureAuthUserCallable = httpsCallable(functions, "ensureAuthUser");
const setAuthUserDisabledCallable = httpsCallable(functions, "setAuthUserDisabled");
const deleteAuthUserCallable = httpsCallable(functions, "deleteAuthUser");
const setFacilityDisabledCallable = httpsCallable(functions, "setFacilityDisabled");
const deleteFacilityCallable = httpsCallable(functions, "deleteFacility");
const DEFAULT_LICENSE_MANAGEMENT_API_URL = `https://us-central1-${app.options.projectId}.cloudfunctions.net/licenseManagementApi`;
const DEFAULT_CONTROL_PLANE_API_URL = `https://us-central1-${app.options.projectId}.cloudfunctions.net/controlPlaneApi`;
const normalizeApiBaseUrl = (value) => normalizeText(value || DEFAULT_LICENSE_MANAGEMENT_API_URL).replace(/\/+$/, "");
const normalizeControlPlaneApiBaseUrl = (value) => normalizeText(value || DEFAULT_CONTROL_PLANE_API_URL).replace(/\/+$/, "");
const LICENSE_MANAGEMENT_API_URL = normalizeApiBaseUrl(process.env.REACT_APP_LICENSE_MANAGEMENT_API_URL);
const CONTROL_PLANE_API_URL = normalizeControlPlaneApiBaseUrl(process.env.REACT_APP_CONTROL_PLANE_API_URL);

const todayDateString = () => new Date().toISOString().slice(0, 10);
const toIsoOrEmpty = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value.toDate === "function") return value.toDate().toISOString();
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? date.toISOString() : "";
};

const addYearsDateString = (years = 1) => {
  const d = new Date();
  d.setFullYear(d.getFullYear() + years);
  return d.toISOString().slice(0, 10);
};

const parseJsonResponse = (text) => {
  try {
    return text ? JSON.parse(text) : {};
  } catch {
    return {};
  }
};

const toLicenseApiError = (response, data, responseText, path) => {
  const serverMessage = normalizeText(data?.error || data?.message);
  let message = serverMessage || `License management failed (${response.status}).`;
  if (response.status === 401) message = serverMessage || "Authentication required. Please sign in again.";
  if (response.status === 403) message = serverMessage || "Permission denied. License Platform actions require HIMTECH/Developer access.";
  if (response.status === 404) message = serverMessage || `License Platform route was not found: ${path}`;
  if (response.status >= 500 && !serverMessage) message = "License Platform service is unavailable. Check cloud function deployment and signing configuration.";
  if (response.status >= 500 && responseText && /^</.test(responseText.trim())) {
    message = "License Platform service returned an HTML error page. Check function URL and deployment region.";
  }
  const err = new Error(message);
  err.status = response.status;
  err.code = data?.code || "LICENSE_PLATFORM_REQUEST_FAILED";
  err.details = data;
  return err;
};

const licenseManagementRequest = async (path, options = {}) => {
  const user = auth.currentUser;
  if (!user) throw new Error("Authentication required. Please sign in to manage signed licenses.");
  const token = await user.getIdToken(Boolean(options.forceRefreshToken));
  const response = await fetch(`${LICENSE_MANAGEMENT_API_URL}${path}`, {
    method: options.method || "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: options.body ? JSON.stringify(options.body) : undefined,
  }).catch((error) => {
    throw new Error(`License Platform service is unreachable: ${error?.message || "network request failed"}`);
  });
  const responseText = await response.text();
  const data = parseJsonResponse(responseText);
  if (!response.ok && !options.forceRefreshToken && [401, 403].includes(response.status)) {
    return licenseManagementRequest(path, { ...options, forceRefreshToken: true });
  }
  if (!response.ok) throw toLicenseApiError(response, data, responseText, path);
  return data;
};

const toControlPlaneApiError = (response, data, responseText, path) => {
  const serverMessage = normalizeText(data?.error || data?.message);
  let message = serverMessage || `Control Plane request failed (${response.status}).`;
  if (response.status === 401) message = serverMessage || "Authentication required. Please sign in again.";
  if (response.status === 403) message = serverMessage || "Permission denied. Control Plane actions require HIMTECH operator access.";
  if (response.status === 404) message = serverMessage || `Control Plane route was not found: ${path}`;
  if (response.status >= 500 && !serverMessage) message = "Control Plane service is unavailable. Check cloud function deployment and server signing configuration.";
  if (response.status >= 500 && responseText && /^</.test(responseText.trim())) {
    message = "Control Plane service returned an HTML error page. Check function URL and deployment region.";
  }
  const err = new Error(message);
  err.status = response.status;
  err.code = data?.code || "CONTROL_PLANE_REQUEST_FAILED";
  err.details = data;
  return err;
};

const controlPlaneRequest = async (path, options = {}) => {
  const user = auth.currentUser;
  if (!user) throw new Error("Authentication required. Please sign in to use the Control Plane.");
  const token = await user.getIdToken(Boolean(options.forceRefreshToken));
  const response = await fetch(`${CONTROL_PLANE_API_URL}${path}`, {
    method: options.method || "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: options.body ? JSON.stringify(options.body) : undefined,
  }).catch((error) => {
    throw new Error(`Control Plane service is unreachable: ${error?.message || "network request failed"}`);
  });
  const responseText = await response.text();
  const data = parseJsonResponse(responseText);
  if (!response.ok && !options.forceRefreshToken && [401, 403].includes(response.status)) {
    return controlPlaneRequest(path, { ...options, forceRefreshToken: true });
  }
  if (!response.ok) throw toControlPlaneApiError(response, data, responseText, path);
  return data;
};

const toSlug = (value) =>
  normalizeText(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);

const toHospitalId = (value, fallbackName = "") => {
  const raw = normalizeText(value) || normalizeText(fallbackName);
  const cleaned = raw
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return cleaned || `CH-${Date.now()}`;
};

const actorLite = (actor) => ({
  id: actor?.id || actor?.uid || actor?.email || "",
  name: actor?.name || actor?.displayName || actor?.email || "System",
  email: actor?.email || "",
});

const permissionsArray = (user) => (Array.isArray(user?.permissions) ? user.permissions : []);

const hasPermission = (user, permission) => {
  if (permissionsArray(user).includes("*") || permissionsArray(user).includes(permission)) return true;
  const permissionsV2 = user?.permissionsV2 || {};
  return permissionsV2[permission] === true || permissionsV2[permission]?.view === true || permissionsV2[permission]?.edit === true;
};

const hasAnyPermission = (user, permissions = []) => permissions.some((permission) => hasPermission(user, permission));

const OPERATIONS_CONSOLE_PERMISSIONS = [
  "operationsConsole.read",
  "hospitals.manage",
  "licenses.manage",
  "subscriptions.manage",
  "rollout.manage",
  "commercialBilling.manage",
  "supportCommands.manage",
  "monitoring.read",
];

const LICENSE_PLATFORM_PERMISSIONS = [
  "licenses.manage",
  "subscriptions.manage",
  "rollout.manage",
  "commercialBilling.manage",
];

const OPERATIONS_CONSOLE_ROLES = new Set([
  "owner",
  "superAdmin",
  "deployment",
  "facilityManager",
  "custom",
  "licenseE2E",
  "licenseE2EAdmin",
]);

const hasOperationsConsoleRole = (user) =>
  OPERATIONS_CONSOLE_ROLES.has(user?.platformAccess) || OPERATIONS_CONSOLE_ROLES.has(user?.platformRole);

const deploymentPreset = (mode = DEPLOYMENT_MODES.ONLINE) => {
  if (mode === DEPLOYMENT_MODES.OFFLINE) {
    return {
      deploymentMode: DEPLOYMENT_MODES.OFFLINE,
      activationType: ACTIVATION_TYPES.CLOUD_ACTIVATION,
      serverRequired: true,
      syncEnabled: false,
      cloudBackupEnabled: false,
      localApiEnabled: true,
      localServerStatus: LOCAL_SERVER_STATUSES.PENDING_ACTIVATION,
    };
  }
  if (mode === DEPLOYMENT_MODES.HYBRID) {
    return {
      deploymentMode: DEPLOYMENT_MODES.HYBRID,
      activationType: ACTIVATION_TYPES.CLOUD_ACTIVATION,
      serverRequired: true,
      syncEnabled: true,
      cloudBackupEnabled: true,
      localApiEnabled: true,
      localServerStatus: LOCAL_SERVER_STATUSES.PENDING_ACTIVATION,
    };
  }
  return {
    deploymentMode: DEPLOYMENT_MODES.ONLINE,
    activationType: ACTIVATION_TYPES.CLOUD_ACTIVATION,
    serverRequired: false,
    syncEnabled: false,
    cloudBackupEnabled: true,
    localApiEnabled: false,
    localServerStatus: LOCAL_SERVER_STATUSES.NOT_INSTALLED,
  };
};

const defaultNumberRange = (prefix = "", overrides = {}) => ({
  prefix,
  padding: 6,
  yearEnabled: false,
  offlineStart: 1,
  offlineEnd: 499999,
  onlineStart: 500000,
  onlineEnd: 999999,
  currentOffline: 0,
  currentOnline: 499999,
  ...overrides,
});

export const buildDefaultNumberRanges = (hospitalId = "", facility = {}) => {
  const patientPrefix = facility.patientIdPrefix ?? "";
  const invoicePrefix = facility.invoicePrefix ?? "";
  return {
    patientNumbers: defaultNumberRange(patientPrefix),
    invoiceNumbers: defaultNumberRange(invoicePrefix),
    receiptNumbers: defaultNumberRange(invoicePrefix),
    labNumbers: defaultNumberRange(""),
    familyFolderNumbers: defaultNumberRange(""),
  };
};

export const normalizeDeploymentProfile = (facility = {}) => {
  const mode = normalizeDeploymentMode(facility.deploymentMode) || DEPLOYMENT_MODES.ONLINE;
  const preset = deploymentPreset(mode);
  const numberRanges = {
    ...buildDefaultNumberRanges(facility.hospitalId || "", facility),
    ...(facility.numberRanges || {}),
  };

  return {
    ...preset,
    activationType: facility.activationType || preset.activationType,
    serverRequired: facility.serverRequired ?? preset.serverRequired,
    syncEnabled: facility.syncEnabled ?? preset.syncEnabled,
    cloudBackupEnabled: facility.cloudBackupEnabled ?? preset.cloudBackupEnabled,
    pacsEnabled: facility.pacsEnabled === true,
    localApiEnabled: facility.localApiEnabled ?? preset.localApiEnabled,
    localServerStatus: facility.localServerStatus || preset.localServerStatus,
    licenseStatus: facility.licenseStatus || LICENSE_STATUSES.TRIAL,
    licenseStartDate: facility.licenseStartDate || todayDateString(),
    licenseExpiryDate: facility.licenseExpiryDate || addYearsDateString(1),
    modulesEnabled: { ...DEFAULT_MODULES_ENABLED, ...(facility.modulesEnabled || {}) },
    serverId: facility.serverId || "",
    activationCode: facility.activationCode || "",
    activationTokenHash: facility.activationTokenHash || "",
    lastActivationAt: facility.lastActivationAt || null,
    lastSeenAt: facility.lastSeenAt || null,
    lastHeartbeatAt: facility.lastHeartbeatAt || null,
    lastSyncAt: facility.lastSyncAt || null,
    appVersion: facility.appVersion || "",
    frontendVersion: facility.frontendVersion || "",
    installerVersion: facility.installerVersion || "",
    deploymentChannel: facility.deploymentChannel || "",
    localApiVersion: facility.localApiVersion || "",
    backupStatus: facility.backupStatus || null,
    lastBackupAt: facility.lastBackupAt || null,
    updateStatus: facility.updateStatus || null,
    latestAvailableVersion: facility.latestAvailableVersion || "",
    queryRuntime: facility.queryRuntime || facility.serverHealth?.queryRuntime || null,
    readModels: facility.readModels || facility.serverHealth?.readModels || null,
    databaseHealth: facility.databaseHealth || facility.serverHealth?.databaseHealth || null,
    cluster: facility.cluster || facility.serverHealth?.cluster || null,
    observability: facility.observability || facility.serverHealth?.observability || null,
    interoperability: facility.interoperability || facility.serverHealth?.interoperability || null,
    resourceEngine: facility.resourceEngine || facility.serverHealth?.resourceEngine || null,
    fhirR4: facility.fhirR4 || facility.serverHealth?.fhirR4 || null,
    syncHealthSummary: facility.syncHealthSummary || null,
    serverHealth: facility.serverHealth || null,
    signedLicenseSummary: facility.signedLicenseSummary || null,
    licenseSource: facility.licenseSource || facility.serverHealth?.licenseSource || "",
    signedLicensePresent: Boolean(facility.signedLicensePresent ?? facility.serverHealth?.signedLicensePresent),
    signatureStatus: facility.signatureStatus || facility.serverHealth?.signatureStatus || "",
    bindingStatus: facility.bindingStatus || facility.serverHealth?.bindingStatus || "",
    licenseKeyId: facility.licenseKeyId || facility.serverHealth?.licenseKeyId || "",
    entitlementVersion: facility.entitlementVersion || facility.serverHealth?.entitlementVersion || "",
    licenseVersion: facility.licenseVersion || facility.serverHealth?.licenseVersion || "",
    licenseVerificationAt: facility.licenseVerificationAt || facility.serverHealth?.licenseVerificationAt || null,
    licenseEnforcementMode: facility.licenseEnforcementMode || facility.serverHealth?.licenseEnforcementMode || "",
    pendingSyncCount: facility.pendingSyncCount || 0,
    configVersion: facility.configVersion || "placeholder-1",
    serverCommands: Array.isArray(facility.serverCommands) ? facility.serverCommands : [],
    numberRanges,
    patientNumberConfig: facility.patientNumberConfig || numberRanges.patientNumbers,
    invoiceNumberConfig: facility.invoiceNumberConfig || numberRanges.invoiceNumbers,
    receiptNumberConfig: facility.receiptNumberConfig || numberRanges.receiptNumbers,
    labNumberConfig: facility.labNumberConfig || numberRanges.labNumbers,
    familyFolderNumberConfig: facility.familyFolderNumberConfig || numberRanges.familyFolderNumbers,
  };
};

const numberValue = (value) => Number(value || 0);

export const validateNumberRanges = (deploymentMode, ranges = {}) => {
  NUMBER_RANGE_TYPES.forEach((key) => {
    const range = ranges[key] || {};
    const offlineStart = numberValue(range.offlineStart);
    const offlineEnd = numberValue(range.offlineEnd);
    const onlineStart = numberValue(range.onlineStart);
    const onlineEnd = numberValue(range.onlineEnd);

    if (deploymentMode !== DEPLOYMENT_MODES.ONLINE && offlineStart >= offlineEnd) {
      throw new Error(`${key}: offline start must be less than offline end.`);
    }
    if (deploymentMode !== DEPLOYMENT_MODES.OFFLINE && onlineStart >= onlineEnd) {
      throw new Error(`${key}: online start must be less than online end.`);
    }
    if (deploymentMode === DEPLOYMENT_MODES.HYBRID && offlineEnd >= onlineStart && onlineEnd >= offlineStart) {
      throw new Error(`${key}: offline and online ranges must not overlap.`);
    }
    if (deploymentMode !== DEPLOYMENT_MODES.ONLINE && offlineEnd - offlineStart < 100) {
      throw new Error(`${key}: offline range is too small. Allocate at least 100 numbers.`);
    }
    if (deploymentMode !== DEPLOYMENT_MODES.OFFLINE && onlineEnd - onlineStart < 100) {
      throw new Error(`${key}: online range is too small. Allocate at least 100 numbers.`);
    }
  });
};

const deploymentPayloadFromFacility = (hospitalId, facility = {}) => {
  const profile = normalizeDeploymentProfile({ ...facility, hospitalId });
  validateNumberRanges(profile.deploymentMode, profile.numberRanges);
  return {
    deploymentMode: profile.deploymentMode,
    activationType: profile.activationType,
    serverRequired: profile.serverRequired,
    syncEnabled: profile.syncEnabled,
    cloudBackupEnabled: profile.cloudBackupEnabled,
    pacsEnabled: profile.pacsEnabled,
    localApiEnabled: profile.localApiEnabled,
    localServerStatus: profile.localServerStatus,
    licenseStatus: profile.licenseStatus,
    licenseStartDate: profile.licenseStartDate,
    licenseExpiryDate: profile.licenseExpiryDate,
    modulesEnabled: profile.modulesEnabled,
    serverId: profile.serverId,
    activationCode: profile.activationCode,
    activationTokenHash: profile.activationTokenHash,
    lastActivationAt: profile.lastActivationAt,
    lastSeenAt: profile.lastSeenAt,
    lastHeartbeatAt: profile.lastHeartbeatAt,
    lastSyncAt: profile.lastSyncAt,
    appVersion: profile.appVersion,
    frontendVersion: profile.frontendVersion,
    installerVersion: profile.installerVersion,
    deploymentChannel: profile.deploymentChannel,
    localApiVersion: profile.localApiVersion,
    backupStatus: profile.backupStatus,
    lastBackupAt: profile.lastBackupAt,
    updateStatus: profile.updateStatus,
    latestAvailableVersion: profile.latestAvailableVersion,
    queryRuntime: profile.queryRuntime,
    readModels: profile.readModels,
    databaseHealth: profile.databaseHealth,
    cluster: profile.cluster,
    observability: profile.observability,
    interoperability: profile.interoperability,
    resourceEngine: profile.resourceEngine,
    fhirR4: profile.fhirR4,
    syncHealthSummary: profile.syncHealthSummary,
    serverHealth: profile.serverHealth,
    pendingSyncCount: profile.pendingSyncCount,
    configVersion: profile.configVersion,
    serverCommands: profile.serverCommands,
    numberRanges: profile.numberRanges,
    patientNumberConfig: profile.numberRanges.patientNumbers,
    invoiceNumberConfig: profile.numberRanges.invoiceNumbers,
    receiptNumberConfig: profile.numberRanges.receiptNumbers,
    labNumberConfig: profile.numberRanges.labNumbers,
    familyFolderNumberConfig: profile.numberRanges.familyFolderNumbers,
  };
};

const appBaseUrl = () =>
  typeof window !== "undefined" && window.location?.origin
    ? window.location.origin
    : "https://clearhealthemr.web.app";

const escapeHtml = (value) =>
  String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const queueEmail = async (email, subject, html) => {
  if (!email) return;
  await setDoc(doc(collection(db, "emailQueue")), {
    to: [email],
    message: {
      subject,
      text: html.replace(/<[^>]+>/g, " "),
      html,
    },
  });
};

const buildOperationsInviteEmail = ({ displayName, email, temporaryPassword, roleName, assignedHospitalIds, actor }) => {
  const consoleUrl = `${appBaseUrl()}/operations-console`;
  const safeName = escapeHtml(displayName || "there");
  const safeEmail = escapeHtml(email);
  const safePassword = escapeHtml(temporaryPassword);
  const safeRole = escapeHtml(roleName || "Operations User");
  const safeActor = escapeHtml(actor?.name || actor?.email || "the ClearHealth team");
  const facilities = assignedHospitalIds?.length ? assignedHospitalIds.join(", ") : "Assigned facilities will appear after access is granted.";

  return `
    <div style="margin:0;padding:0;background:#f1f5f9;font-family:Arial,Helvetica,sans-serif;color:#0f172a;">
      <div style="max-width:680px;margin:0 auto;padding:28px 16px;">
        <div style="background:#111827;border-radius:18px 18px 0 0;padding:26px 28px;color:#ffffff;">
          <div style="font-size:13px;letter-spacing:.08em;text-transform:uppercase;opacity:.78;">ClearHealth Operations Console</div>
          <h1 style="margin:8px 0 0;font-size:26px;line-height:1.25;">You have been invited to the Operations Console</h1>
          <p style="margin:8px 0 0;font-size:15px;opacity:.9;">${safeActor} created your platform access as ${safeRole}.</p>
        </div>

        <div style="background:#ffffff;border:1px solid #e2e8f0;border-top:0;border-radius:0 0 18px 18px;padding:28px;">
          <p style="margin:0 0 16px;font-size:15px;line-height:1.7;">Hello ${safeName},</p>
          <p style="margin:0 0 18px;font-size:15px;line-height:1.7;">
            Your ClearHealth Operations Console account is ready. Use it to support deployments, enter assigned facilities, and help complete onboarding tasks based on your access level.
          </p>

          <div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:14px;padding:18px;margin:20px 0;">
            <div style="font-size:13px;color:#64748b;margin-bottom:6px;">Login email</div>
            <div style="font-size:16px;font-weight:700;color:#0f172a;word-break:break-all;">${safeEmail}</div>
            <div style="height:14px;"></div>
            <div style="font-size:13px;color:#64748b;margin-bottom:6px;">Temporary password</div>
            <div style="display:inline-block;background:#ecfeff;border:1px solid #99f6e4;color:#115e59;border-radius:10px;padding:10px 14px;font-size:18px;font-weight:800;letter-spacing:.04em;">${safePassword}</div>
            <div style="height:14px;"></div>
            <div style="font-size:13px;color:#64748b;margin-bottom:6px;">Facility scope</div>
            <div style="font-size:14px;color:#334155;">${escapeHtml(facilities)}</div>
          </div>

          <h2 style="margin:22px 0 10px;font-size:18px;color:#0f172a;">Next steps</h2>
          <ol style="margin:0 0 22px;padding-left:20px;color:#334155;font-size:15px;line-height:1.75;">
            <li>Open the Operations Console using the button below.</li>
            <li>Sign in with your email and temporary password.</li>
            <li>Change your password when prompted.</li>
            <li>Review the facilities available to you.</li>
            <li>Use Enter beside a facility to support that hospital without requesting staff login credentials.</li>
          </ol>

          <p style="margin:24px 0;">
            <a href="${consoleUrl}" style="display:inline-block;background:#111827;color:#ffffff;text-decoration:none;padding:13px 18px;border-radius:12px;font-weight:700;">Open Operations Console</a>
          </p>

          <div style="border-top:1px solid #e2e8f0;margin-top:24px;padding-top:16px;font-size:13px;color:#64748b;line-height:1.6;">
            Keep this account secure. Do not share your temporary password or your signed-in session.
          </div>
        </div>
      </div>
    </div>
  `;
};

const chunk = (arr, size) => {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

const buildOperatorFlags = (accessProfile, selectedFlags = {}) => {
  const profile = accessProfile || "deployment";
  const base = {
    isOwner: false,
    isSuperAdmin: false,
    canManageFacilities: false,
    canModifyFacilities: false,
    canOnboardFacilities: false,
    canEnterFacilities: false,
    platformRole: profile,
  };

  if (profile === "owner") {
    return {
      ...base,
      isOwner: true,
      canManageFacilities: true,
      canModifyFacilities: !!selectedFlags.canModifyFacilities,
      canOnboardFacilities: true,
      canEnterFacilities: true,
    };
  }

  if (profile === "superAdmin") {
    return {
      ...base,
      isSuperAdmin: true,
      canManageFacilities: true,
      canModifyFacilities: !!selectedFlags.canModifyFacilities,
      canOnboardFacilities: true,
      canEnterFacilities: true,
    };
  }

  if (profile === "facilityManager") {
    return {
      ...base,
      canManageFacilities: true,
      canEnterFacilities: true,
    };
  }

  if (profile === "custom") {
    return {
      ...base,
      isOwner: !!selectedFlags.isOwner,
      isSuperAdmin: !!selectedFlags.isSuperAdmin,
      canManageFacilities: !!selectedFlags.canManageFacilities,
      canModifyFacilities: !!selectedFlags.canModifyFacilities,
      canOnboardFacilities: !!selectedFlags.canOnboardFacilities,
      canEnterFacilities: !!selectedFlags.canEnterFacilities,
    };
  }

  return {
    ...base,
    canOnboardFacilities: true,
    canEnterFacilities: true,
  };
};

export const isOperationsUser = (user) => {
  if (!user) return false;
  return Boolean(
    user.isOwner ||
      user.isSuperAdmin ||
      hasAnyPermission(user, OPERATIONS_CONSOLE_PERMISSIONS) ||
      user.canManageFacilities ||
      user.canModifyFacilities ||
      user.canOnboardFacilities ||
      user.canEnterFacilities ||
      hasOperationsConsoleRole(user)
  );
};

export const canManageLicensePlatform = (user) =>
  Boolean(
    user?.isOwner ||
      user?.isSuperAdmin ||
      user?.platformAccess === "owner" ||
      user?.platformAccess === "superAdmin" ||
      user?.platformRole === "owner" ||
      user?.platformRole === "superAdmin" ||
      hasAnyPermission(user, LICENSE_PLATFORM_PERMISSIONS)
  );

export const canManageAllFacilities = (user) =>
  Boolean(
    user?.isOwner ||
      user?.isSuperAdmin ||
      user?.canManageFacilities ||
      user?.canModifyFacilities ||
      user?.platformAccess === "owner" ||
      user?.platformRole === "owner"
  );

export const canModifyFacilities = (user) =>
  Boolean(user?.canModifyFacilities);

export const listFacilitiesForOperator = async (operator) => {
  if (!isOperationsUser(operator)) return [];

  const facilities = new Map();
  const addSnapshot = (snap) => {
    snap.docs.forEach((d) => facilities.set(d.id, { id: d.id, ...d.data() }));
  };

  if (canManageAllFacilities(operator)) {
    const snap = await getDocs(query(collection(db, HOSPITAL_COLLECTION), orderBy("hospitalName", "asc")));
    addSnapshot(snap);
    return Array.from(facilities.values());
  }

  const uid = operator?.id || operator?.uid || "";
  const email = normalizeEmail(operator?.email);
  const assignedHospitalIds = unique(operator?.assignedHospitalIds || operator?.facilityIds || []);

  const reads = [];
  if (uid) {
    reads.push(
      getDocs(query(collection(db, HOSPITAL_COLLECTION), where("assignedOperatorIds", "array-contains", uid)))
    );
  }
  if (email) {
    reads.push(
      getDocs(query(collection(db, HOSPITAL_COLLECTION), where("assignedOperatorEmails", "array-contains", email)))
    );
  }
  for (const ids of chunk(assignedHospitalIds, 10)) {
    reads.push(getDocs(query(collection(db, HOSPITAL_COLLECTION), where("hospitalId", "in", ids))));
  }

  const snaps = await Promise.all(reads);
  snaps.forEach(addSnapshot);

  return Array.from(facilities.values()).sort((a, b) =>
    (a.hospitalName || "").localeCompare(b.hospitalName || "")
  );
};

export const listOperationsUsers = async (operator) => {
  if (!operator || (!canManageAllFacilities(operator) && !operator.isOwner)) {
    return operator ? [operator] : [];
  }

  const users = new Map();
  const addSnapshot = (snap) => {
    snap.docs.forEach((d) => users.set(d.id, { id: d.id, ...d.data() }));
  };

  const usersRef = collection(db, USERS_COLLECTION);
  const reads = [
    getDocs(query(usersRef, where("isOwner", "==", true))),
    getDocs(query(usersRef, where("isSuperAdmin", "==", true))),
    getDocs(query(usersRef, where("canManageFacilities", "==", true))),
    getDocs(query(usersRef, where("canModifyFacilities", "==", true))),
    getDocs(query(usersRef, where("canOnboardFacilities", "==", true))),
    getDocs(query(usersRef, where("canEnterFacilities", "==", true))),
  ];

  const snaps = await Promise.all(reads);
  snaps.forEach(addSnapshot);

  return Array.from(users.values())
    .filter((item) => canModifyFacilities(operator) || !item.isOwner)
    .sort((a, b) =>
      (a.displayName || a.name || a.email || "").localeCompare(b.displayName || b.name || b.email || "")
    );
};

export const saveOperationsUser = async (operatorUser, actor) => {
  if (!actor?.isOwner) {
    throw new Error("Only an owner can manage console users.");
  }

  const email = normalizeEmail(operatorUser?.email);
  const fName = normalizeText(operatorUser?.fName);
  const lName = normalizeText(operatorUser?.lName);
  const phoneNumber = normalizeText(operatorUser?.phoneNumber);

  if (!email || !fName || !lName) {
    throw new Error("First name, last name, and email are required.");
  }

  let existingUser = null;
  if (operatorUser?.id) {
    const snap = await getDoc(doc(db, USERS_COLLECTION, operatorUser.id));
    if (snap.exists()) existingUser = { id: snap.id, ...snap.data() };
  }
  if (!existingUser) {
    const existing = await fetchUsersByEmail(email).catch(() => []);
    existingUser = Array.isArray(existing) ? existing[0] : existing;
  }

  const userRef = existingUser?.id
    ? doc(db, USERS_COLLECTION, existingUser.id)
    : doc(collection(db, USERS_COLLECTION));

  const flags = buildOperatorFlags(operatorUser.accessProfile, operatorUser.flags);
  if (flags.isOwner && !canModifyFacilities(actor)) {
    throw new Error("Only users with canModifyFacilities can create or update owner console users.");
  }

  const displayName = `${lName} ${fName}${operatorUser?.oName ? ` ${operatorUser.oName}` : ""}`.trim();
  const assignedHospitalIds = unique(operatorUser.assignedHospitalIds || []);
  const tempPassword = normalizePhonePassword(phoneNumber);
  if (!existingUser?.id && tempPassword.length !== 11) {
    throw new Error("Enter the user's complete 11-digit phone number. It will be used as the temporary password.");
  }
  const authResult = await ensureAuthUserCallable({
    email,
    password: existingUser?.id ? "" : tempPassword,
    displayName,
    disabled: operatorUser?.status === "Disabled" || !!operatorUser?.authDisabled,
  });
  const authUid = authResult?.data?.uid || existingUser?.authUid || "";
  const searchKeywords = [
    ...generateKeywordsByCharacters(fName),
    ...generateKeywordsByCharacters(lName),
    ...generateKeywords(email),
    ...generateKeywords(phoneNumber),
  ];

  const payload = {
    id: userRef.id,
    fName,
    lName,
    oName: operatorUser?.oName || "",
    name: displayName,
    displayName,
    email,
    authUid,
    authDisabled: operatorUser?.status === "Disabled" || !!operatorUser?.authDisabled,
    phoneNumber,
    status: operatorUser?.status || "Active",
    hospitalId: "",
    assignedHospitalIds,
    facilityIds: assignedHospitalIds,
    needsPasswordChange: existingUser?.id ? !!existingUser.needsPasswordChange : true,
    roleName: flags.isOwner
      ? "Owner"
      : flags.isSuperAdmin
        ? "Super Admin"
        : flags.canManageFacilities
          ? "Operations Manager"
          : "Deployment Partner",
    ...flags,
    platformAccess: flags.isOwner ? "owner" : flags.isSuperAdmin ? "superAdmin" : flags.platformRole,
    enrolledBy: actorLite(actor),
    searchKeywords,
    lastUpdated: serverTimestamp(),
  };

  await setDoc(
    userRef,
    existingUser?.id ? payload : { ...payload, createdAt: serverTimestamp(), createdBy: actorLite(actor) },
    { merge: true }
  );

  if (!existingUser?.id) {
    const emailBody = buildOperationsInviteEmail({
      displayName,
      email,
      temporaryPassword: tempPassword,
      roleName: payload.roleName,
      assignedHospitalIds,
      actor: actorLite(actor),
    });
    await queueEmail(email, "Welcome to ClearHealth Operations Console", emailBody).catch((error) => {
      console.error("Failed to queue operations console welcome email:", error?.message || error);
    });
  }

  return { id: userRef.id, ...payload };
};

export const setOperationsUserStatus = async (operatorUser, disabled, actor) => {
  if (!canModifyFacilities(actor)) {
    throw new Error("You do not have access to disable or re-enable console users.");
  }
  if (operatorUser?.isOwner) {
    throw new Error("Owner accounts cannot be disabled from the console.");
  }

  const email = normalizeEmail(operatorUser?.email);
  if (!email && !operatorUser?.authUid) {
    throw new Error("A user email or Auth UID is required.");
  }

  const result = await setAuthUserDisabledCallable({
    email,
    uid: operatorUser?.authUid || "",
    disabled,
  });

  if (operatorUser?.id) {
    await setDoc(
      doc(db, USERS_COLLECTION, operatorUser.id),
      {
        status: disabled ? "Disabled" : "Active",
        authDisabled: disabled,
        authUid: result?.data?.uid || operatorUser?.authUid || "",
        lastUpdated: serverTimestamp(),
        lastUpdatedBy: actorLite(actor),
      },
      { merge: true }
    );
  }

  return result?.data || { success: true, disabled };
};

export const deleteOperationsUser = async (operatorUser, actor) => {
  if (!canModifyFacilities(actor)) {
    throw new Error("You do not have access to delete console users.");
  }
  if (operatorUser?.isOwner) {
    throw new Error("Owner accounts cannot be deleted from the console.");
  }

  const result = await deleteAuthUserCallable({
    userId: operatorUser?.id || "",
    uid: operatorUser?.authUid || "",
    email: normalizeEmail(operatorUser?.email),
    hospitalId: operatorUser?.hospitalId || "",
  });
  return result?.data || { success: true };
};

export const setFacilityStatus = async (facility, disabled, actor) => {
  if (!canModifyFacilities(actor)) {
    throw new Error("You do not have access to modify facilities.");
  }
  const result = await setFacilityDisabledCallable({
    hospitalId: facility?.hospitalId || "",
    disabled,
  });
  return result?.data || { success: true, disabled };
};

export const removeFacility = async (facility, confirmHospitalId, actor) => {
  if (!canModifyFacilities(actor)) {
    throw new Error("You do not have access to delete facilities.");
  }
  const result = await deleteFacilityCallable({
    hospitalId: facility?.hospitalId || "",
    confirmHospitalId,
  });
  return result?.data || { success: true };
};

export const findFacilityByHospitalId = async (hospitalId) => {
  const cleanId = toHospitalId(hospitalId);
  const snap = await getDocs(
    query(collection(db, HOSPITAL_COLLECTION), where("hospitalId", "==", cleanId), limit(1))
  );
  if (snap.empty) return null;
  const d = snap.docs[0];
  return { id: d.id, ...d.data() };
};

export const findFacilityBySlug = async (slug) => {
  const cleanSlug = toSlug(slug);
  if (!cleanSlug) return null;
  const snap = await getDocs(
    query(collection(db, HOSPITAL_COLLECTION), where("slug", "==", cleanSlug), limit(1))
  );
  if (snap.empty) return null;
  const d = snap.docs[0];
  return { id: d.id, ...d.data() };
};

const ensurePatientIdConfig = async (hospitalId, prefix, patientNumberConfig = null) => {
  await setDoc(
    doc(db, "patientIdConfig", hospitalId),
    {
      hospitalId,
      prefix: patientNumberConfig?.prefix ?? prefix ?? "",
      counter: Number(patientNumberConfig?.currentOnline ?? 0),
      offlineCounter: Number(patientNumberConfig?.currentOffline ?? 0),
      rangeStart: Number(patientNumberConfig?.onlineStart ?? 1),
      rangeEnd: Number(patientNumberConfig?.onlineEnd ?? 999999),
      offlineRangeStart: Number(patientNumberConfig?.offlineStart ?? 1),
      offlineRangeEnd: Number(patientNumberConfig?.offlineEnd ?? 499999),
      EDcounter: 0,
      useYear: patientNumberConfig?.yearEnabled === true,
      yearPlacement: patientNumberConfig?.yearEnabled === true ? "afterPrefix" : "none",
      padding: Number(patientNumberConfig?.padding || 6),
      updatedAt: new Date().toISOString(),
    },
    { merge: true }
  );
};

const ensureSettings = async (hospitalId, facility, actor) => {
  const deploymentProfile = normalizeDeploymentProfile({ ...facility, hospitalId });
  await setDoc(
    doc(db, "settings", hospitalId),
    {
      clinicDetails: {
        name: facility.hospitalName || "",
        address: facility.address || "",
        phone: facility.phone || "",
        email: facility.email || "",
        hours: facility.hours || "",
        emergencyContact: facility.emergencyContact || "",
      },
      branding: {
        logo: facility.logo || null,
        favicon: facility.favicon || null,
        primaryColor: facility.primaryColor || "#0f766e",
        secondaryColor: facility.secondaryColor || "#0f172a",
      },
      preferences: {
        theme: "light",
        language: "English",
        dateFormat: "DD-MM-YYYY",
        timeFormat: "24-hour",
        timeZone: facility.timeZone || "GMT+1",
      },
      patientIdSettings: {
        idPrefix: facility.patientIdPrefix ?? "",
        includeYear: false,
        lastGeneratedId: "",
      },
      appointmentSettings: { defaultDuration: 30, enableReminders: false, reminderInterval: 24 },
      billingSettings: { invoicePrefix: facility.invoicePrefix ?? "", taxRate: 0 },
      deploymentSettings: {
        deploymentMode: deploymentProfile.deploymentMode,
        activationType: deploymentProfile.activationType,
        serverRequired: deploymentProfile.serverRequired,
        syncEnabled: deploymentProfile.syncEnabled,
        cloudBackupEnabled: deploymentProfile.cloudBackupEnabled,
        localApiEnabled: deploymentProfile.localApiEnabled,
        localServerStatus: deploymentProfile.localServerStatus,
        licenseStatus: deploymentProfile.licenseStatus,
        licenseExpiryDate: deploymentProfile.licenseExpiryDate,
      },
      numberRanges: deploymentProfile.numberRanges,
      notificationSettings: { emailNotifications: false, smsNotifications: false, pushNotifications: false },
      securitySettings: { sessionTimeout: 30, enable2FA: false },
      backupSettings: { enableBackup: false, backupFrequency: "daily" },
      auditLogs: { enableAuditLogs: true, retentionPeriod: 12 },
      duplicateCheck: { checkByName: true, checkByPhone: true, checkByDOB: false },
      hospitalId,
      lastUpdated: serverTimestamp(),
      updatedBy: actorLite(actor),
    },
    { merge: true }
  );
};

const ensureGopdClinic = async (hospitalId, facility, actor) => {
  if (!facility.createGopdClinic) return null;

  const existing = await fetchClinics(hospitalId).catch(() => []);
  const found = existing.find((c) => /gopd|general outpatient/i.test(c.name || ""));
  if (found) return found;

  const departmentId = "general_outpatient";
  await setDoc(
    doc(db, "departments", `${hospitalId}_${departmentId}`),
    {
      id: `${hospitalId}_${departmentId}`,
      name: "General Outpatient Department",
      description: "General outpatient care and first-contact consultations",
      status: "Active",
      isPredefined: false,
      hospitalId,
      lastUpdated: new Date(),
    },
    { merge: true }
  );

  const clinic = {
    name: facility.gopdClinicName || "GOPD Clinic",
    location: facility.gopdLocation || "",
    departmentId: `${hospitalId}_${departmentId}`,
    departmentName: "General Outpatient Department",
    workingDays: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
    startTime: "08:00",
    endTime: "17:00",
    workingHours: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"].map((day) => ({
      day,
      startTime: "08:00",
      endTime: "17:00",
    })),
  };

  const response = await addClinic(hospitalId, clinic, actorLite(actor));
  return { ...clinic, id: response.id };
};

const ensureStarterServices = async (hospitalId, services, actor) => {
  const existing = await fetchServices(hospitalId).catch(() => []);
  const existingNames = new Set(existing.map((s) => normalizeText(s.serviceName).toLowerCase()));
  const created = [];

  for (const item of services || []) {
    const serviceName = normalizeText(item.serviceName);
    if (!serviceName || existingNames.has(serviceName.toLowerCase())) continue;
    const payload = {
      serviceName,
      serviceCategory: item.serviceCategory || "General",
      departmentId: item.departmentId || "",
      type: item.type || "general",
      price: Number(item.price || 0),
    };
    const response = await addService(hospitalId, payload, actorLite(actor));
    created.push({ ...payload, id: response.id });
  }

  return created;
};

const createInitialAdmin = async (hospitalId, admin, actor, hospitalDetails) => {
  if (!admin?.email || !admin?.fName || !admin?.lName || !admin?.phoneNumber) return null;

  const existing = await fetchUsersByEmail(admin.email).catch(() => []);
  if (existing && !Array.isArray(existing)) return { skipped: true, reason: "email-exists", user: existing };
  if (Array.isArray(existing) && existing.length > 0) return { skipped: true, reason: "email-exists", user: existing[0] };

  await savePredefinedRolesToFirestore();
  const payload = {
    fName: admin.fName,
    lName: admin.lName,
    oName: admin.oName || "",
    gender: admin.gender || "",
    email: normalizeEmail(admin.email),
    phoneNumber: admin.phoneNumber,
    employeeId: admin.employeeId || "",
    departmentId: "",
    departmentName: "",
    clinicId: "",
    clinicName: "",
    roleId: "Admin",
    roleName: "Admin",
    employmentType: admin.employmentType || "Full-time",
    status: "Active",
  };

  return handleSaveUser(hospitalId, payload, null, actorLite(actor), hospitalDetails);
};

export const onboardFacility = async ({ facility, adminUser, services }, actor) => {
  const hospitalName = normalizeText(facility?.hospitalName);
  if (!hospitalName) throw new Error("Hospital name is required.");

  const hospitalId = toHospitalId(facility?.hospitalId, hospitalName);
  const existing = await findFacilityByHospitalId(hospitalId);
  if (existing && !facility?.allowExisting) {
    throw new Error(`A facility with hospital ID ${hospitalId} already exists.`);
  }

  const assignedOperatorIds = unique([
    ...(facility?.assignedOperatorIds || []),
    ...(actor?.id || actor?.uid ? [actor.id || actor.uid] : []),
  ]);
  const assignedOperatorEmails = unique([
    ...(facility?.assignedOperatorEmails || []).map(normalizeEmail),
    ...(actor?.email ? [normalizeEmail(actor.email)] : []),
  ]);
  const deploymentPayload = deploymentPayloadFromFacility(hospitalId, facility);

  const hospitalPayload = {
    hospitalId,
    hospitalName,
    nameLower: hospitalName.toLowerCase(),
    slug: toSlug(facility?.slug || hospitalName),
    state: normalizeText(facility?.state),
    stateCode: normalizeText(facility?.stateCode).toUpperCase(),
    address: facility?.address || "",
    phone: facility?.phone || "",
    email: normalizeEmail(facility?.email),
    logo: facility?.logo || "",
    status: facility?.status || "Active",
    onboardingStatus: "Ready",
    allowBiometricTerminals: Number(facility?.allowBiometricTerminals || 0),
    assignedOperatorIds,
    assignedOperatorEmails,
    ...deploymentPayload,
    updatedAt: serverTimestamp(),
    updatedBy: actorLite(actor),
  };

  const hospitalRef = existing?.id
    ? doc(db, HOSPITAL_COLLECTION, existing.id)
    : doc(collection(db, HOSPITAL_COLLECTION));

  await setDoc(
    hospitalRef,
    existing ? hospitalPayload : { ...hospitalPayload, createdAt: serverTimestamp(), createdBy: actorLite(actor) },
    { merge: true }
  );

  const hospitalDetails = { id: hospitalRef.id, ...hospitalPayload };
  await ensureSettings(hospitalId, hospitalDetails, actor);
  await ensurePatientIdConfig(hospitalId, facility?.patientIdPrefix ?? "", deploymentPayload.patientNumberConfig);
  const clinic = await ensureGopdClinic(hospitalId, { ...facility, hospitalName, hospitalId }, actor);
  const createdServices = await ensureStarterServices(hospitalId, services, actor);
  const admin = await createInitialAdmin(hospitalId, adminUser, actor, hospitalDetails);

  return {
    facility: { ...hospitalDetails, id: hospitalRef.id },
    clinic,
    services: createdServices,
    admin,
  };
};

const findFacilityDocument = async (facilityId) => {
  if (!facilityId) throw new Error("Facility ID is required.");
  const directRef = doc(db, HOSPITAL_COLLECTION, facilityId);
  const directSnap = await getDoc(directRef);
  if (directSnap.exists()) return { ref: directRef, data: { id: directSnap.id, ...directSnap.data() } };

  const found = await findFacilityByHospitalId(facilityId);
  if (!found?.id) throw new Error("Facility was not found.");
  return { ref: doc(db, HOSPITAL_COLLECTION, found.id), data: found };
};

export const updateFacilityDetails = async (facility, updates, actor) => {
  if (!canManageAllFacilities(actor) && !actor?.canOnboardFacilities) {
    throw new Error("You do not have access to edit facilities.");
  }

  const facilityId = facility?.id || facility?.hospitalId;
  const { ref, data } = await findFacilityDocument(facilityId);
  const hospitalId = data.hospitalId || facility?.hospitalId;
  const hospitalName = normalizeText(updates?.hospitalName);
  if (!hospitalName) throw new Error("Hospital name is required.");

  const nextFacility = {
    ...data,
    ...updates,
    hospitalId,
    hospitalName,
  };
  const patientIdPrefix = normalizeText(updates?.patientIdPrefix);
  const invoicePrefix = normalizeText(updates?.invoicePrefix);
  const currentRanges = {
    ...buildDefaultNumberRanges(hospitalId, nextFacility),
    ...(nextFacility.numberRanges || {}),
  };
  nextFacility.numberRanges = {
    ...currentRanges,
    patientNumbers: { ...(currentRanges.patientNumbers || {}), prefix: patientIdPrefix },
    invoiceNumbers: { ...(currentRanges.invoiceNumbers || {}), prefix: invoicePrefix },
    receiptNumbers: { ...(currentRanges.receiptNumbers || {}), prefix: invoicePrefix },
  };
  const deploymentPayload = deploymentPayloadFromFacility(hospitalId, nextFacility);
  const hospitalPayload = {
    hospitalName,
    nameLower: hospitalName.toLowerCase(),
    slug: toSlug(updates?.slug || data.slug || hospitalName),
    state: normalizeText(updates?.state),
    stateCode: normalizeText(updates?.stateCode).toUpperCase(),
    address: normalizeText(updates?.address),
    phone: normalizeText(updates?.phone),
    email: normalizeEmail(updates?.email),
    allowBiometricTerminals: Number(updates?.allowBiometricTerminals || 0),
    patientIdPrefix,
    invoicePrefix,
    ...deploymentPayload,
    updatedAt: serverTimestamp(),
    updatedBy: actorLite(actor),
  };

  await setDoc(ref, hospitalPayload, { merge: true });

  const settingsRef = doc(db, "settings", hospitalId);
  const settingsSnap = await getDoc(settingsRef);
  const existingSettings = settingsSnap.exists() ? settingsSnap.data() : {};
  await setDoc(
    settingsRef,
    {
      clinicDetails: {
        ...(existingSettings.clinicDetails || {}),
        name: hospitalName,
        address: hospitalPayload.address,
        phone: hospitalPayload.phone,
        email: hospitalPayload.email,
      },
      patientIdSettings: {
        ...(existingSettings.patientIdSettings || {}),
        idPrefix: hospitalPayload.patientIdPrefix,
      },
      billingSettings: {
        ...(existingSettings.billingSettings || {}),
        invoicePrefix: hospitalPayload.invoicePrefix,
      },
      deploymentSettings: {
        ...(existingSettings.deploymentSettings || {}),
        deploymentMode: deploymentPayload.deploymentMode,
        activationType: deploymentPayload.activationType,
        serverRequired: deploymentPayload.serverRequired,
        syncEnabled: deploymentPayload.syncEnabled,
        cloudBackupEnabled: deploymentPayload.cloudBackupEnabled,
        localApiEnabled: deploymentPayload.localApiEnabled,
        localServerStatus: deploymentPayload.localServerStatus,
        licenseStatus: deploymentPayload.licenseStatus,
        licenseExpiryDate: deploymentPayload.licenseExpiryDate,
      },
      numberRanges: deploymentPayload.numberRanges,
      hospitalId,
      lastUpdated: serverTimestamp(),
      updatedBy: actorLite(actor),
    },
    { merge: true }
  );

  await setDoc(
    doc(db, "patientIdConfig", hospitalId),
    {
      hospitalId,
      prefix: hospitalPayload.patientIdPrefix,
      useYear: deploymentPayload.patientNumberConfig?.yearEnabled === true,
      padding: Number(deploymentPayload.patientNumberConfig?.padding || 6),
      lastUpdated: serverTimestamp(),
      updatedBy: actorLite(actor),
    },
    { merge: true }
  );

  return { success: true, facility: { ...data, ...hospitalPayload, id: ref.id, hospitalId } };
};

const randomTokenPart = () => {
  const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  const bytes = new Uint8Array(6);
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
    return Array.from(bytes, (byte) => alphabet[byte % alphabet.length]).join("");
  }
  return Math.random().toString(36).slice(2, 8).toUpperCase();
};

export const generateActivationCode = async (facility, actor) => {
  if (!canModifyFacilities(actor) && !actor?.canOnboardFacilities) {
    throw new Error("You do not have access to generate activation codes.");
  }
  const facilityId = facility?.id || facility?.hospitalId;
  const { data } = await findFacilityDocument(facilityId);
  const result = await licenseManagementRequest("/activation-codes/issue", {
    method: "POST",
    body: {
      hospitalId: data.hospitalId || facility?.hospitalId || facilityId,
      deploymentMode: data.deploymentMode || facility?.deploymentMode || DEPLOYMENT_MODES.OFFLINE,
      purpose: "FIRST_ACTIVATION",
      reason: "Operations Console activation code generation.",
    },
  });
  return {
    success: true,
    activationCode: result.activationCode,
    activationCodeId: result.activationCodeId,
    activationTokenHash: result.activationTokenHash,
    codePrefix: result.codePrefix,
    expiresAt: result.expiresAt,
    oneTimeDisplay: true,
    offlineLicenseFile: {
      format: "CLEARHEALTH_OFFLINE_LICENSE_PLACEHOLDER",
      hospitalId: data.hospitalId || "",
      deploymentMode: data.deploymentMode || DEPLOYMENT_MODES.ONLINE,
      licenseStatus: data.licenseStatus || LICENSE_STATUSES.TRIAL,
      licenseExpiryDate: data.licenseExpiryDate || "",
      generatedAt: new Date().toISOString(),
      generatedBy: actorLite(actor),
      signed: false,
      encrypted: false,
    },
  };
};

export const updateFacilityDeploymentProfile = async (facilityId, profile, actor) => {
  if (!canModifyFacilities(actor) && !actor?.canOnboardFacilities) {
    throw new Error("You do not have access to update deployment profiles.");
  }
  const { ref, data } = await findFacilityDocument(facilityId);
  const payload = deploymentPayloadFromFacility(data.hospitalId || facilityId, { ...data, ...profile });
  await setDoc(
    ref,
    {
      ...payload,
      updatedAt: serverTimestamp(),
      updatedBy: actorLite(actor),
    },
    { merge: true }
  );
  return { success: true, data: payload };
};

export const updateFacilityLicenseStatus = async (facilityId, status, actor) => {
  if (!canModifyFacilities(actor)) {
    throw new Error("You do not have access to update license status.");
  }
  if (!Object.values(LICENSE_STATUSES).includes(status)) throw new Error("Invalid license status.");
  const { ref } = await findFacilityDocument(facilityId);
  await setDoc(
    ref,
    {
      licenseStatus: status,
      updatedAt: serverTimestamp(),
      updatedBy: actorLite(actor),
    },
    { merge: true }
  );
  return { success: true, licenseStatus: status };
};

export const queueFacilityServerCommand = async (facilityId, command, actor) => {
  if (!canModifyFacilities(actor) && !actor?.canOnboardFacilities && !hasPermission(actor, "supportCommands.manage")) {
    throw new Error("You do not have access to queue server commands.");
  }
  const { ref, data } = await findFacilityDocument(facilityId);
  const type = normalizeText(command?.type).toUpperCase();
  const allowed = [
    "REFRESH_CONFIG",
    "FORCE_HEARTBEAT",
    "SYNC_NOW",
    "SHOW_ADMIN_MESSAGE",
    "REQUEST_BACKUP_NOW",
    "GENERATE_SUPPORT_BUNDLE",
    "CHECK_FOR_UPDATE",
    "PREPARE_UPDATE",
    "PAUSE_SYNC",
    "RESUME_SYNC",
  ];
  if (!allowed.includes(type)) throw new Error("This server command is not available from the Operations Console.");
  const payload = command?.payload && typeof command.payload === "object" ? command.payload : {};
  if (type === "SHOW_ADMIN_MESSAGE" && !normalizeText(payload.message)) {
    throw new Error("Enter a message for the local admin.");
  }

  const now = new Date().toISOString();
  const item = {
    id: `${type}-${Date.now()}-${randomTokenPart()}`,
    type,
    status: "PENDING",
    payload,
    createdAt: now,
    createdBy: actorLite(actor),
    acknowledgedAt: null,
    completedAt: null,
    error: "",
  };
  const serverCommands = [...(Array.isArray(data.serverCommands) ? data.serverCommands : []), item].slice(-50);
  const configVersion = `config-${Date.now()}`;
  await setDoc(
    ref,
    {
      serverCommands,
      configVersion,
      updatedAt: serverTimestamp(),
      updatedBy: actorLite(actor),
    },
    { merge: true }
  );
  return { success: true, command: item, configVersion };
};

export const resetFacilityActivation = async (facilityId, actor) => {
  if (!canModifyFacilities(actor)) {
    throw new Error("You do not have access to reset facility activation.");
  }
  const { ref, data } = await findFacilityDocument(facilityId);
  await setDoc(
    ref,
    {
      activationCode: "",
      activationTokenHash: "",
      serverId: "",
      localApiVersion: "",
      lastActivationAt: null,
      lastSeenAt: null,
      lastHeartbeatAt: null,
      activationAcceptedAt: null,
      activationMachineFingerprint: "",
      serverHealth: null,
      pendingSyncCount: 0,
      serverCommands: [],
      configVersion: `config-${Date.now()}`,
      localServerStatus: data.localApiEnabled ? LOCAL_SERVER_STATUSES.PENDING_ACTIVATION : LOCAL_SERVER_STATUSES.NOT_INSTALLED,
      updatedAt: serverTimestamp(),
      updatedBy: actorLite(actor),
    },
    { merge: true }
  );
  return { success: true };
};

// Placeholder Developer Console contract for future HTTPS Cloud Functions.
// A callable/HTTP function can wrap this to serve local-api activation verification.
export const verifyFacilityActivation = async (hospitalId, activationCode, serverPayload = {}) => {
  const normalizedHospitalId = toHospitalId(hospitalId);
  const code = normalizeText(activationCode);
  const { ref, data } = await findFacilityDocument(normalizedHospitalId);
  const profile = normalizeDeploymentProfile({ ...data, hospitalId: data.hospitalId || normalizedHospitalId });

  if (!code || code !== profile.activationCode) {
    throw new Error("Activation code is invalid for this hospital.");
  }
  if (!profile.localApiEnabled) {
    throw new Error("Local API is not enabled for this hospital.");
  }
  if (![LICENSE_STATUSES.TRIAL, LICENSE_STATUSES.ACTIVE].includes(profile.licenseStatus)) {
    throw new Error(`License is ${profile.licenseStatus}. Activation is not allowed.`);
  }

  const serverId = normalizeText(serverPayload.serverId) || profile.serverId || "";
  const localApiVersion = normalizeText(serverPayload.localApiVersion) || profile.localApiVersion || "";
  await setDoc(
    ref,
    {
      serverId,
      localApiVersion,
      localServerStatus: LOCAL_SERVER_STATUSES.ACTIVE,
      lastActivationAt: serverTimestamp(),
      lastSeenAt: serverTimestamp(),
      activationVerifiedAt: serverTimestamp(),
      activationServerPayload: serverPayload || {},
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );

  const hospitalProfile = {
    hospitalId: data.hospitalId || normalizedHospitalId,
    hospitalName: data.hospitalName || data.name || data.hospitalId || normalizedHospitalId,
    name: data.hospitalName || data.name || data.hospitalId || normalizedHospitalId,
    address: data.address || "",
    phone: data.phone || "",
    email: data.email || "",
    state: data.state || "",
    stateCode: data.stateCode || "",
    deploymentMode: profile.deploymentMode,
    activationType: profile.activationType,
    licenseStatus: profile.licenseStatus,
    licenseStartDate: profile.licenseStartDate,
    licenseExpiryDate: profile.licenseExpiryDate,
    modulesEnabled: profile.modulesEnabled,
    numberRanges: profile.numberRanges,
    patientNumberConfig: profile.patientNumberConfig,
    invoiceNumberConfig: profile.invoiceNumberConfig,
    receiptNumberConfig: profile.receiptNumberConfig,
    labNumberConfig: profile.labNumberConfig,
    familyFolderNumberConfig: profile.familyFolderNumberConfig,
    cloudBackupEnabled: profile.cloudBackupEnabled,
    syncEnabled: profile.syncEnabled,
    pacsEnabled: profile.pacsEnabled,
    localApiEnabled: profile.localApiEnabled,
    serverId,
    localApiVersion,
  };

  return { success: true, hospitalProfile };
};

// Placeholder Developer Console contract for future HTTPS Cloud Functions.
// A callable/HTTP function can wrap this to receive local-api server heartbeats.
export const updateFacilityHeartbeat = async (hospitalId, heartbeatPayload = {}) => {
  const normalizedHospitalId = toHospitalId(hospitalId || heartbeatPayload.hospitalId);
  const { ref, data } = await findFacilityDocument(normalizedHospitalId);
  const serverId = normalizeText(heartbeatPayload.serverId) || data.serverId || "";
  const localApiVersion = normalizeText(heartbeatPayload.localApiVersion) || data.localApiVersion || "";
  await setDoc(
    ref,
    {
      serverId,
      localApiVersion,
      appVersion: normalizeText(heartbeatPayload.appVersion) || data.appVersion || "",
      frontendVersion: normalizeText(heartbeatPayload.frontendVersion) || data.frontendVersion || "",
      installerVersion: normalizeText(heartbeatPayload.installerVersion) || data.installerVersion || "",
      deploymentChannel: normalizeText(heartbeatPayload.deploymentChannel) || data.deploymentChannel || "",
      backupStatus: heartbeatPayload.backupStatus || data.backupStatus || null,
      lastBackupAt: heartbeatPayload.lastBackupAt || data.lastBackupAt || null,
      updateStatus: heartbeatPayload.updateStatus || data.updateStatus || null,
      latestAvailableVersion: normalizeText(heartbeatPayload.latestAvailableVersion) || data.latestAvailableVersion || "",
      workerRuntime: heartbeatPayload.workerRuntime || data.workerRuntime || null,
      queryRuntime: heartbeatPayload.queryRuntime || data.queryRuntime || null,
      readModels: heartbeatPayload.readModels || data.readModels || null,
      databaseHealth: heartbeatPayload.databaseHealth || data.databaseHealth || null,
      cluster: heartbeatPayload.cluster || data.cluster || null,
      observability: heartbeatPayload.observability || data.observability || null,
      interoperability: heartbeatPayload.interoperability || data.interoperability || null,
      resourceEngine: heartbeatPayload.resourceEngine || data.resourceEngine || null,
      fhirR4: heartbeatPayload.fhirR4 || data.fhirR4 || null,
      syncHealthSummary: heartbeatPayload.syncHealthSummary || data.syncHealthSummary || null,
      licenseSource: heartbeatPayload.licenseSummary?.licenseSource || heartbeatPayload.licenseSummary?.entitlementVersion || data.licenseSource || "",
      signedLicensePresent: Boolean(heartbeatPayload.licenseSummary?.signedLicensePresent ?? data.signedLicensePresent),
      signatureStatus: normalizeText(heartbeatPayload.licenseSummary?.signatureStatus) || data.signatureStatus || "",
      bindingStatus: normalizeText(heartbeatPayload.licenseSummary?.bindingStatus) || data.bindingStatus || "",
      licenseKeyId: normalizeText(heartbeatPayload.licenseSummary?.licenseKeyId) || data.licenseKeyId || "",
      entitlementVersion: normalizeText(heartbeatPayload.licenseSummary?.entitlementVersion) || data.entitlementVersion || "",
      licenseVersion: normalizeText(heartbeatPayload.licenseSummary?.licenseVersion) || data.licenseVersion || "",
      licenseVerificationAt: heartbeatPayload.licenseSummary?.verificationAt || data.licenseVerificationAt || null,
      licenseEnforcementMode: normalizeText(heartbeatPayload.licenseSummary?.enforcementMode) || data.licenseEnforcementMode || "",
      localServerStatus: LOCAL_SERVER_STATUSES.ACTIVE,
      lastSeenAt: serverTimestamp(),
      lastSyncAt: heartbeatPayload.lastSyncAt || data.lastSyncAt || null,
      heartbeat: {
        ...heartbeatPayload,
        receivedAt: new Date().toISOString(),
      },
      serverHealth: {
        ...(data.serverHealth || {}),
        ...heartbeatPayload,
        workerRuntime: heartbeatPayload.workerRuntime || data.workerRuntime || data.serverHealth?.workerRuntime || null,
        queryRuntime: heartbeatPayload.queryRuntime || data.queryRuntime || data.serverHealth?.queryRuntime || null,
        readModels: heartbeatPayload.readModels || data.readModels || data.serverHealth?.readModels || null,
        databaseHealth: heartbeatPayload.databaseHealth || data.databaseHealth || data.serverHealth?.databaseHealth || null,
        cluster: heartbeatPayload.cluster || data.cluster || data.serverHealth?.cluster || null,
        observability: heartbeatPayload.observability || data.observability || data.serverHealth?.observability || null,
        interoperability: heartbeatPayload.interoperability || data.interoperability || data.serverHealth?.interoperability || null,
        resourceEngine: heartbeatPayload.resourceEngine || data.resourceEngine || data.serverHealth?.resourceEngine || null,
        fhirR4: heartbeatPayload.fhirR4 || data.fhirR4 || data.serverHealth?.fhirR4 || null,
        receivedAt: new Date().toISOString(),
      },
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
  return { success: true, hospitalId: data.hospitalId || normalizedHospitalId, serverId, localApiVersion };
};

export const updateFacilityAssignments = async (facility, assignments, actor) => {
  if (!facility?.id) throw new Error("Facility document ID is required.");
  const assignedOperatorIds = unique(assignments?.assignedOperatorIds || []);
  const assignedOperatorEmails = unique((assignments?.assignedOperatorEmails || []).map(normalizeEmail));

  await setDoc(
    doc(db, HOSPITAL_COLLECTION, facility.id),
    {
      assignedOperatorIds,
      assignedOperatorEmails,
      updatedAt: serverTimestamp(),
      updatedBy: actorLite(actor),
    },
    { merge: true }
  );

  const selectedUsers = assignments?.selectedUsers || [];
  await Promise.all(
    selectedUsers
      .filter((u) => u?.id)
      .map((u) => {
        const nextHospitalIds = unique([...(u.assignedHospitalIds || []), facility.hospitalId]);
        return setDoc(
          doc(db, USERS_COLLECTION, u.id),
          {
            assignedHospitalIds: nextHospitalIds,
            facilityIds: nextHospitalIds,
            canEnterFacilities: true,
            lastUpdated: serverTimestamp(),
            updatedBy: actorLite(actor),
          },
          { merge: true }
        );
      })
  );
};

export const issueSignedLicense = async (facility, overrides = {}) => {
  const profile = normalizeDeploymentProfile(facility || {});
  return licenseManagementRequest("/licenses/issue", {
    method: "POST",
    body: {
      hospitalId: profile.hospitalId,
      hospitalName: profile.hospitalName,
      edition: overrides.edition || profile.signedLicenseSummary?.edition || "PROFESSIONAL",
      deploymentMode: overrides.deploymentMode || profile.deploymentMode,
      status: overrides.status || profile.licenseStatus || LICENSE_STATUSES.TRIAL,
      expiresAt: toIsoOrEmpty(overrides.expiresAt || profile.licenseExpiryDate),
      graceUntil: toIsoOrEmpty(overrides.graceUntil),
      serverId: overrides.serverId || profile.serverId || "",
      updateChannel: overrides.updateChannel || profile.deploymentChannel || "pilot",
      reason: overrides.reason || "Issued from Operations Console.",
    },
  });
};

export const reissueSignedLicense = async (licenseId, overrides = {}) =>
  licenseManagementRequest(`/licenses/${encodeURIComponent(licenseId)}/reissue`, {
    method: "POST",
    body: {
      ...overrides,
      reason: overrides.reason || "Reissued from Operations Console.",
    },
  });

export const getHospitalSignedLicenses = async (hospitalId) =>
  licenseManagementRequest(`/licenses/hospital/${encodeURIComponent(hospitalId)}`);

export const getSignedLicenseIssuanceHistory = async (licenseId) =>
  licenseManagementRequest(`/licenses/${encodeURIComponent(licenseId)}/issuance-history`);

export const getActivationDiagnosticsForFacility = async (facilityId) =>
  licenseManagementRequest(`/activation-diagnostics/facilities/${encodeURIComponent(facilityId)}`);

export const getActivationDiagnosticsForDeployment = async (deploymentId) =>
  licenseManagementRequest(`/activation-diagnostics/deployments/${encodeURIComponent(deploymentId)}`);

export const getActivationDiagnosticsForServerInstallation = async (serverInstallationId) =>
  licenseManagementRequest(`/activation-diagnostics/server-installations/${encodeURIComponent(serverInstallationId)}`);

export const getActivationDiagnosticsForAttempt = async (activationAttemptId) =>
  licenseManagementRequest(`/activation-diagnostics/attempts/${encodeURIComponent(activationAttemptId)}`);

export const getActivationDiagnosticsForCorrelation = async (correlationId) =>
  licenseManagementRequest(`/activation-diagnostics/correlations/${encodeURIComponent(correlationId)}`);

export const processOfflineRenewalRequest = async (renewalRequest, overrides = {}) =>
  licenseManagementRequest("/licenses/offline-renewal/process", {
    method: "POST",
    body: {
      renewalRequest,
      ...overrides,
      reason: overrides.reason || "Processed offline renewal request from Operations Console.",
    },
  });

export const approveLicenseTransfer = async (licenseId, payload = {}) =>
  licenseManagementRequest(`/licenses/${encodeURIComponent(licenseId)}/transfer/approve`, {
    method: "POST",
    body: { ...payload, confirmed: payload.confirmed === true },
  });

export const rejectLicenseTransfer = async (licenseId, payload = {}) =>
  licenseManagementRequest(`/licenses/${encodeURIComponent(licenseId)}/transfer/reject`, {
    method: "POST",
    body: payload,
  });

export const getLicenseTransferHistory = async (licenseId) =>
  licenseManagementRequest(`/licenses/${encodeURIComponent(licenseId)}/transfer-history`);

export const suspendSignedLicense = async (licenseId, payload = {}) =>
  licenseManagementRequest(`/licenses/${encodeURIComponent(licenseId)}/suspend`, {
    method: "POST",
    body: { ...payload, confirmed: payload.confirmed === true },
  });

export const reactivateSignedLicense = async (licenseId, payload = {}) =>
  licenseManagementRequest(`/licenses/${encodeURIComponent(licenseId)}/reactivate`, {
    method: "POST",
    body: { ...payload, confirmed: payload.confirmed === true },
  });

export const revokeSignedLicense = async (licenseId, payload = {}) =>
  licenseManagementRequest(`/licenses/${encodeURIComponent(licenseId)}/revoke`, {
    method: "POST",
    body: { ...payload, confirmed: payload.confirmed === true },
  });

export const validateOfflineActivationChallenge = async (challenge) =>
  licenseManagementRequest("/offline-activation/challenges/validate", {
    method: "POST",
    body: { challenge },
  });

export const importOfflineActivationChallenge = async (payload = {}) =>
  licenseManagementRequest("/offline-activation/challenges/import", {
    method: "POST",
    body: payload,
  });

export const issueOfflineActivationPackage = async (payload = {}) =>
  licenseManagementRequest("/offline-activation/packages/issue", {
    method: "POST",
    body: payload,
  });

export const revokeOfflineActivationPackage = async (payload = {}) =>
  licenseManagementRequest("/offline-activation/packages/revoke", {
    method: "POST",
    body: { ...payload, confirmed: payload.confirmed === true },
  });

export const confirmOfflineActivationPackage = async (payload = {}) =>
  licenseManagementRequest("/offline-activation/confirm", {
    method: "POST",
    body: payload,
  });

export const listServerLifecycleRequests = async () =>
  licenseManagementRequest("/server-lifecycle/requests");

export const getServerLifecycleRequest = async (requestId) =>
  licenseManagementRequest(`/server-lifecycle/requests/${encodeURIComponent(requestId)}`);

export const createServerLifecycleRequest = async (payload = {}) =>
  licenseManagementRequest("/server-lifecycle/requests", {
    method: "POST",
    body: payload,
  });

export const submitServerLifecycleRequest = async (requestId, payload = {}) =>
  licenseManagementRequest(`/server-lifecycle/requests/${encodeURIComponent(requestId)}/submit`, {
    method: "POST",
    body: payload,
  });

export const approveServerLifecycleRequest = async (requestId, payload = {}) =>
  licenseManagementRequest(`/server-lifecycle/requests/${encodeURIComponent(requestId)}/approve`, {
    method: "POST",
    body: payload,
  });

export const rejectServerLifecycleRequest = async (requestId, payload = {}) =>
  licenseManagementRequest(`/server-lifecycle/requests/${encodeURIComponent(requestId)}/reject`, {
    method: "POST",
    body: payload,
  });

export const cancelServerLifecycleRequest = async (requestId, payload = {}) =>
  licenseManagementRequest(`/server-lifecycle/requests/${encodeURIComponent(requestId)}/cancel`, {
    method: "POST",
    body: payload,
  });

export const issueServerLifecycleAuthorization = async (requestId, payload = {}) =>
  licenseManagementRequest(`/server-lifecycle/requests/${encodeURIComponent(requestId)}/issue-authorization`, {
    method: "POST",
    body: payload,
  });

export const revokeServerLifecycleAuthorization = async (requestId, payload = {}) =>
  licenseManagementRequest(`/server-lifecycle/requests/${encodeURIComponent(requestId)}/revoke-authorization`, {
    method: "POST",
    body: payload,
  });

export const confirmServerLifecycleRequest = async (requestId, payload = {}) =>
  licenseManagementRequest(`/server-lifecycle/requests/${encodeURIComponent(requestId)}/confirm`, {
    method: "POST",
    body: payload,
  });

export const reconcileServerLifecycleRequest = async (requestId, payload = {}) =>
  licenseManagementRequest(`/server-lifecycle/requests/${encodeURIComponent(requestId)}/reconcile`, {
    method: "POST",
    body: payload,
  });

export const getServerLifecycleHistory = async (serverInstallationId) =>
  licenseManagementRequest(`/server-lifecycle/servers/${encodeURIComponent(serverInstallationId)}/history`);

export const getServerLifecycleDiagnostics = async (serverInstallationId) =>
  licenseManagementRequest(`/server-lifecycle/servers/${encodeURIComponent(serverInstallationId)}/diagnostics`);

export const createSubscription = async (payload = {}) =>
  licenseManagementRequest("/subscriptions/create", {
    method: "POST",
    body: payload,
  });

export const getHospitalSubscriptions = async (hospitalId) =>
  licenseManagementRequest(`/subscriptions/hospital/${encodeURIComponent(hospitalId)}`);

export const getSubscription = async (subscriptionId) =>
  licenseManagementRequest(`/subscriptions/${encodeURIComponent(subscriptionId)}`);

export const getSubscriptionHistory = async (subscriptionId) =>
  licenseManagementRequest(`/subscriptions/${encodeURIComponent(subscriptionId)}/history`);

export const activateSubscription = async (subscriptionId, payload = {}) =>
  licenseManagementRequest(`/subscriptions/${encodeURIComponent(subscriptionId)}/activate`, {
    method: "POST",
    body: payload,
  });

export const renewSubscription = async (subscriptionId, payload = {}) =>
  licenseManagementRequest(`/subscriptions/${encodeURIComponent(subscriptionId)}/renew`, {
    method: "POST",
    body: payload,
  });

export const changeSubscriptionPlan = async (subscriptionId, payload = {}) =>
  licenseManagementRequest(`/subscriptions/${encodeURIComponent(subscriptionId)}/change-plan`, {
    method: "POST",
    body: payload,
  });

export const startSubscriptionGrace = async (subscriptionId, payload = {}) =>
  licenseManagementRequest(`/subscriptions/${encodeURIComponent(subscriptionId)}/start-grace`, {
    method: "POST",
    body: payload,
  });

export const suspendSubscription = async (subscriptionId, payload = {}) =>
  licenseManagementRequest(`/subscriptions/${encodeURIComponent(subscriptionId)}/suspend`, {
    method: "POST",
    body: payload,
  });

export const cancelSubscription = async (subscriptionId, payload = {}) =>
  licenseManagementRequest(`/subscriptions/${encodeURIComponent(subscriptionId)}/cancel`, {
    method: "POST",
    body: payload,
  });

export const expireSubscription = async (subscriptionId, payload = {}) =>
  licenseManagementRequest(`/subscriptions/${encodeURIComponent(subscriptionId)}/expire`, {
    method: "POST",
    body: payload,
  });

export const applySubscriptionToLicense = async (subscriptionId, payload = {}) =>
  licenseManagementRequest(`/subscriptions/${encodeURIComponent(subscriptionId)}/apply-to-license`, {
    method: "POST",
    body: payload,
  });

export const getLicenseRollouts = async () =>
  licenseManagementRequest("/rollout");

export const getLicenseRollout = async (hospitalId) =>
  licenseManagementRequest(`/rollout/${encodeURIComponent(hospitalId)}`);

export const updateLicenseRollout = async (hospitalId, action, payload = {}) =>
  licenseManagementRequest(`/rollout/${encodeURIComponent(hospitalId)}/${encodeURIComponent(action)}`, {
    method: "POST",
    body: { ...payload, confirmed: payload.confirmed === true },
  });

export const prepareLicenseRollout = async (hospitalId, payload = {}) =>
  updateLicenseRollout(hospitalId, "prepare", payload);

export const approveLicenseRolloutAuditOnly = async (hospitalId, payload = {}) =>
  updateLicenseRollout(hospitalId, "approve-audit-only", payload);

export const approveLicenseRolloutWarnOnly = async (hospitalId, payload = {}) =>
  updateLicenseRollout(hospitalId, "approve-warn-only", payload);

export const approveLicenseRolloutCapabilityEnforcement = async (hospitalId, payload = {}) =>
  updateLicenseRollout(hospitalId, "approve-capability-enforcement", payload);

export const approveLicenseRolloutModuleEnforcement = async (hospitalId, payload = {}) =>
  updateLicenseRollout(hospitalId, "approve-module-enforcement", payload);

export const pauseLicenseRollout = async (hospitalId, payload = {}) =>
  updateLicenseRollout(hospitalId, "pause", payload);

export const rollbackLicenseRollout = async (hospitalId, payload = {}) =>
  updateLicenseRollout(hospitalId, "rollback", payload);

export const exemptLicenseRollout = async (hospitalId, payload = {}) =>
  updateLicenseRollout(hospitalId, "exempt", payload);

export const createCommercialSubscriptionInvoice = async (payload = {}) =>
  licenseManagementRequest("/commercial-invoices/create", {
    method: "POST",
    body: payload,
  });

export const updateCommercialSubscriptionInvoiceStatus = async (invoiceId, action, payload = {}) =>
  licenseManagementRequest(`/commercial-invoices/${encodeURIComponent(invoiceId)}/${encodeURIComponent(action)}`, {
    method: "POST",
    body: { ...payload, confirmed: payload.confirmed === true },
  });

export const recordCommercialSubscriptionPayment = async (payload = {}) =>
  licenseManagementRequest("/commercial-payments/record", {
    method: "POST",
    body: payload,
  });

export const reverseCommercialSubscriptionPayment = async (paymentId, payload = {}) =>
  licenseManagementRequest(`/commercial-payments/${encodeURIComponent(paymentId)}/reverse`, {
    method: "POST",
    body: { ...payload, confirmed: payload.confirmed === true },
  });

export const getControlPlaneTrustedKeys = async () =>
  controlPlaneRequest("/.well-known/clearhealth-keys.json");

export const getControlPlaneFleet = async () =>
  controlPlaneRequest("/control-plane/fleet");

export const getControlPlaneDeploymentServers = async (deploymentId) =>
  controlPlaneRequest(`/control-plane/deployments/${encodeURIComponent(deploymentId)}/servers`);

export const getControlPlaneServer = async (serverInstallationId) =>
  controlPlaneRequest(`/control-plane/servers/${encodeURIComponent(serverInstallationId)}`);

export const getControlPlaneServerHealth = async (serverInstallationId) =>
  controlPlaneRequest(`/control-plane/servers/${encodeURIComponent(serverInstallationId)}/health`);

export const getControlPlaneServerTimeline = async (serverInstallationId) =>
  controlPlaneRequest(`/control-plane/servers/${encodeURIComponent(serverInstallationId)}/timeline`);

export const issueControlPlaneCommand = async (payload = {}) =>
  controlPlaneRequest("/control-plane/commands", {
    method: "POST",
    body: payload,
  });

export const revokeControlPlaneServerCredential = async (payload = {}) =>
  controlPlaneRequest("/control-plane/auth/server/revoke", {
    method: "POST",
    body: payload,
  });
